import { format, parseISO, formatDistanceToNow } from "date-fns";
import { useT } from "@/i18n/I18nProvider";

// Booking lifecycle (Phase 2B):
//   awaiting_staff_approval -> awaiting_customer_confirmation -> confirmed
//   -> ongoing -> completed / cancelled / no_show
// `is_hold` / `change_requested` are flags on awaiting_staff_approval, not
// separate statuses. `cancelled_by` / `cancellation_reason` carry
// cancellation provenance. See getBookingStatusMeta() below for how these
// compose into a single display label.
export const BLOCKING_BOOKING_STATUSES = new Set([
  "awaiting_staff_approval", "awaiting_customer_confirmation", "confirmed", "ongoing",
]);
export const TERMINAL_BOOKING_STATUSES = new Set(["completed", "cancelled", "no_show"]);

// Map booking status → i18n tooltip key. Source for fallback English text
// lives inside /app/frontend/src/i18n/translations.js (`tip.*` keys).
const STATUS_TIP_KEYS = {
  awaiting_staff_approval: "tip.awaiting_staff_approval",
  awaiting_customer_confirmation: "tip.pending_client_confirmation",
  reconfirmation_required: "tip.reconfirmation_required",
  confirmed: "tip.client_confirmed",
  ongoing: "tip.ongoing",
  at_risk: "tip.at_risk",
  affected_action_needed: "tip.affected_action_needed",
  completed: "tip.completed",
  cancelled: "tip.cancelled",
  no_show: "tip.no_show",
};

export const BOOKING_STATUS_META = {
  draft: { label: "Draft", bg: "bg-slate-100", text: "text-slate-700", border: "border-slate-200" },
  awaiting_staff_approval: { label: "Awaiting Approval", bg: "bg-amber-100", text: "text-amber-800", border: "border-amber-200" },
  awaiting_customer_confirmation: { label: "Pending Confirmation", bg: "bg-orange-100", text: "text-orange-800", border: "border-orange-200" },
  confirmed: { label: "Customer Confirmed", bg: "bg-emerald-100", text: "text-emerald-800", border: "border-emerald-200" },
  ongoing: { label: "Ongoing", bg: "bg-indigo-100", text: "text-indigo-800", border: "border-indigo-200" },
  at_risk: { label: "At Risk", bg: "bg-red-100", text: "text-red-800", border: "border-red-200" },
  affected_action_needed: { label: "Action Needed", bg: "bg-red-100", text: "text-red-800", border: "border-red-200" },
  completed: { label: "Completed", bg: "bg-blue-100", text: "text-blue-800", border: "border-blue-200" },
  cancelled: { label: "Cancelled", bg: "bg-stone-100", text: "text-stone-700", border: "border-stone-200" },
  no_show: { label: "No Show", bg: "bg-rose-900", text: "text-rose-50", border: "border-rose-950" },
};

/**
 * Compose the precise booking-status label from status + the optional
 * hold/change-request/cancellation fields. Falls back to the generic
 * per-status label from BOOKING_STATUS_META when the extra fields aren't
 * passed (or don't refine anything for that status), so untouched call
 * sites keep working: just slightly less precise.
 */
export function getBookingStatusMeta(status, { isHold, changeRequested, cancelledBy, cancellationReason, reconfirmationRequired } = {}) {
  const base = BOOKING_STATUS_META[status] || {
    label: status, bg: "bg-slate-100", text: "text-slate-700", border: "border-slate-200",
  };
  if (status === "awaiting_staff_approval") {
    if (isHold) return { ...base, label: "Temporary Hold" };
    if (changeRequested) return { ...base, label: "Change Requested" };
  }
  if (status === "awaiting_customer_confirmation" && reconfirmationRequired) {
    return { ...base, label: "Reconfirmation Needed" };
  }
  if (status === "cancelled") {
    if (cancellationReason === "hold_expired") return { ...base, label: "Cancelled: Hold Expired" };
    if (cancelledBy === "customer") return { ...base, label: "Cancelled by Customer" };
  }
  return base;
}

export const VEHICLE_STATUS_META = {
  available: { label: "Available", bg: "bg-emerald-100", text: "text-emerald-800" },
  booked: { label: "Booked", bg: "bg-blue-100", text: "text-blue-800" },
  in_use: { label: "In Use", bg: "bg-indigo-100", text: "text-indigo-800" },
  returned_needs_check: { label: "Needs Inspection", bg: "bg-amber-100", text: "text-amber-800" },
  maintenance: { label: "Maintenance", bg: "bg-yellow-100", text: "text-yellow-800" },
  damaged: { label: "Damaged", bg: "bg-red-100", text: "text-red-800" },
  unavailable: { label: "Unavailable", bg: "bg-slate-100", text: "text-slate-800" },
  retired: { label: "Retired", bg: "bg-stone-100", text: "text-stone-700" },
};

export function StatusBadge({ status, type = "booking", "data-testid": testId,
                              isHold, changeRequested, cancelledBy, cancellationReason, reconfirmationRequired }) {
  const { t } = useT();
  const meta = type === "vehicle"
    ? (VEHICLE_STATUS_META[status] || { label: status, bg: "bg-slate-100", text: "text-slate-700", border: "border-slate-200" })
    : getBookingStatusMeta(status, { isHold, changeRequested, cancelledBy, cancellationReason, reconfirmationRequired });
  const tipKey = type === "booking"
    ? (status === "awaiting_customer_confirmation" && reconfirmationRequired
        ? STATUS_TIP_KEYS.reconfirmation_required
        : STATUS_TIP_KEYS[status])
    : null;
  const tip = tipKey ? t(tipKey) : undefined;
  return (
    <span
      data-testid={testId}
      title={tip}
      className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium border ${meta.bg} ${meta.text} ${meta.border || ""}`}
    >
      {meta.label}
    </span>
  );
}

export function RiskPill({ risk }) {
  if (!risk || risk === "ok") return null;
  const cls = risk === "at_risk"
    ? "bg-red-100 text-red-800 border-red-200"
    : "bg-amber-100 text-amber-800 border-amber-200";
  const label = risk === "at_risk" ? "Conflict" : "Watch";
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium border ${cls}`}>
      {label}
    </span>
  );
}

export function fmtDate(iso) {
  if (!iso) return "";
  try {
    return format(parseISO(iso), "MMM d, yyyy h:mm a");
  } catch {
    return iso;
  }
}

export function fmtRelative(iso) {
  if (!iso) return "";
  try {
    return formatDistanceToNow(parseISO(iso), { addSuffix: true });
  } catch {
    return iso;
  }
}

export function fmtCurrency(n) {
  if (n == null) return "-";
  return new Intl.NumberFormat("en-PH", { style: "currency", currency: "PHP" }).format(n);
}
